import { Box, Button, Menu, MenuItem, Typography } from '@mui/material';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import type { CaseDto } from '~/backend/connector-next-api/connector-next-schemas';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import DisabledHint from '~/components/Tooltips/DisabledHint';

type CaseSelectionBarProps = {
    /** How many datasets are ticked. */
    count: number;
    /** Cases the ticked datasets can be added to: those not archived. */
    cases: readonly CaseDto[];
    /** The case in view; ticked datasets can be removed from it. */
    selectedCaseId: string | undefined;
    /** Absent when the user may not change cases. */
    onAddTo?: (caseId: string) => void;
    /** Absent when the user may not change cases. */
    onRemove?: () => void;
    /** Why removing the ticked datasets is refused, as a message id. */
    removeRefusal?: string;
    onClear: () => void;
    busy: boolean;
};

/**
 * What can be done with the ticked datasets besides making a new case of
 * them: add them to a case, or take them out of the case in view, when the
 * user may change cases; and clear the ticks.
 */
export default function CaseSelectionBar({
    count,
    cases,
    selectedCaseId,
    onAddTo,
    onRemove,
    removeRefusal,
    onClear,
    busy,
}: CaseSelectionBarProps) {
    const intl = useIntl();
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const addable = onAddTo ? cases.filter((c) => c.id !== selectedCaseId) : [];

    return (
        <Box
            role="toolbar"
            aria-label={intl.formatMessage({
                id: 'patientList.cases.selectionBar',
            })}
            sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
                px: 3,
                py: 1,
                bgcolor: 'action.hover',
                borderBottom: 1,
                borderColor: 'divider',
            }}
        >
            <Typography component="span" sx={{ fontSize: 13, mr: 1 }}>
                {intl.formatMessage(
                    { id: 'patientList.cases.ticked' },
                    { count }
                )}
            </Typography>
            {addable.length > 0 && (
                <Button
                    size="small"
                    variant="outlined"
                    disabled={busy}
                    endIcon={<ChevronDown size={14} />}
                    aria-haspopup="menu"
                    onClick={(event) => setMenuAnchor(event.currentTarget)}
                >
                    {intl.formatMessage({ id: 'patientList.cases.addTo' })}
                </Button>
            )}
            {selectedCaseId !== undefined &&
                onRemove &&
                (removeRefusal ? (
                    <DisabledHint
                        title={intl.formatMessage({ id: removeRefusal })}
                    >
                        <Button size="small" variant="outlined" disabled>
                            {intl.formatMessage({
                                id: 'patientList.cases.remove',
                            })}
                        </Button>
                    </DisabledHint>
                ) : (
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={busy}
                        onClick={onRemove}
                    >
                        {intl.formatMessage({
                            id: 'patientList.cases.remove',
                        })}
                    </Button>
                ))}
            <Button
                size="small"
                color="inherit"
                onClick={onClear}
                sx={{ ml: 'auto' }}
            >
                {intl.formatMessage({ id: 'patientList.cases.clearTicks' })}
            </Button>
            <Menu
                anchorEl={menuAnchor}
                open={menuAnchor !== null}
                onClose={() => setMenuAnchor(null)}
                MenuListProps={{ dense: true }}
            >
                {addable.map((c) => (
                    <MenuItem
                        key={c.id}
                        onClick={() => {
                            setMenuAnchor(null);
                            if (c.id) {
                                onAddTo?.(c.id);
                            }
                        }}
                    >
                        <SensitiveInfoTypography>
                            {c.name}
                        </SensitiveInfoTypography>
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
}
